// Extension tiers — how much editor a surface gets.
//
// ── Why this file exists ────────────────────────────────────────────────────
// Until now `TiptapEditorProps` had no `extensions` prop, so every consumer
// got the same ~25 extensions, the same 27-item slash catalogue and the same
// floating toolbar. That uniformity is a feature for the nine document-shaped
// consumers — and the exact reason the two small editing surfaces in the app
// (the sticky note, the slide text box) never became Tiptap: a full-page slash
// menu inside a 320×200 note is not a smaller version of the right thing, it
// is the wrong thing.
//
// A tier is therefore NOT "pick your own extensions". It is a small, closed
// set of named sizes the package supports and tests. Consumers choose a size;
// nobody assembles a private editor that then drifts from the shared one.
//
// ── The chrome travels with the tier, deliberately ──────────────────────────
// BlockHandle, FloatingToolbar and the slash menu are written against the FULL
// extension set: the toolbar calls `setFontFamily`, `setFontSize`,
// `setLineHeight`, `setTextAlign`; the slash catalogue inserts tables, images,
// callouts and field blocks. Rendering any of them over a lean tier would call
// commands that do not exist — a TypeError at click time, not a missing
// button. So the chrome is not separately configurable: it is derived from the
// tier, and only `full` gets it.

import type { AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';

/**
 * How much editor this surface gets.
 *
 * - `full`  — every extension and every menu. The default, and what all nine
 *   document consumers use. Unchanged by this file.
 * - `basic` — paragraphs, headings, lists, checklists, links and the text
 *   marks (bold/italic/underline/strike/code, colour, highlight). No slash
 *   menu, no block handle, no floating toolbar: the host draws its own small
 *   toolbar and calls editor commands. Sized for a sticky note.
 * - `plain` — one flat run of text with bold/italic/underline and nothing
 *   structural: no headings, no lists, no quotes, no rules. Sized for a slide
 *   text box, where size/weight/alignment/colour live on the box itself.
 */
export type EditorTier = 'full' | 'basic' | 'plain';

/** Which of the package's own overlays render at a given tier. */
export interface EditorChrome {
  blockHandle: boolean;
  floatingToolbar: boolean;
  slashMenu: boolean;
  statusBar: boolean;
}

export const CHROME_BY_TIER: Record<EditorTier, EditorChrome> = {
  full: { blockHandle: true, floatingToolbar: true, slashMenu: true, statusBar: true },
  basic: { blockHandle: false, floatingToolbar: false, slashMenu: false, statusBar: false },
  plain: { blockHandle: false, floatingToolbar: false, slashMenu: false, statusBar: false },
};

/**
 * The extension list for a tier below `full`.
 *
 * `full` is deliberately absent: its list lives inside `TiptapEditor` next to
 * the comments explaining why each piece is configured the way it is, and
 * copying it here would make two lists that drift.
 *
 * `placeholder` is passed in rather than built here because the component
 * feeds it a language-aware, per-node-type resolver held in a ref. Omitted,
 * the editor simply shows no placeholder — which is what a serializer wants.
 *
 * Note on StarterKit v3: it already bundles `link` and `underline`, so neither
 * is imported again here. (The `full` list still registers both separately —
 * that predates this file and is left alone rather than folded in blind.)
 */
export function buildLeanExtensions(
  tier: Exclude<EditorTier, 'full'>,
  placeholder?: AnyExtension,
): AnyExtension[] {
  const base: AnyExtension[] =
    tier === 'basic'
      ? [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            // No CodeBlockGam at this tier, and a bare code block inside a
            // 320px note is not worth the schema.
            codeBlock: false,
            link: { openOnClick: false, HTMLAttributes: { class: 'gam-link', rel: 'noopener noreferrer' } },
          }),
          TaskList.configure({ HTMLAttributes: { class: 'gam-tasklist' } }),
          TaskItem.configure({ nested: true, HTMLAttributes: { class: 'gam-taskitem' } }),
          TextStyle,
          Color,
          Highlight.configure({ multicolor: true, HTMLAttributes: { class: 'gam-highlight' } }),
        ]
      : [
          StarterKit.configure({
            heading: false,
            codeBlock: false,
            code: false,
            blockquote: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            listKeymap: false,
            horizontalRule: false,
            link: false,
            trailingNode: false,
          }),
        ];

  return placeholder ? [...base, placeholder] : base;
}
